<template>
  <div v-if="isReady" class="popup-box verify-code-popup-box">
    <a class="popup-close" href="javascript:;" @click.prevent="$emit('close')">
      <i class="fa-solid fa-xmark" aria-hidden="true" />
    </a>
    <div class="form-popup popup-padding">
      <div class="title-block">
        <h4 class="verify-popup-title">
          {{ $t(`Bao.user_verify_code_title_${mode}`) }}
        </h4>
        <hr class="title-hr">
      </div>

      <p class="verify-desc">
        {{ $t(`Bao.user_verify_code_desc_${mode}`) }}
      </p>

      <div class="verify-input-wrap">
        <input
          v-model="validateCode"
          v-validate="'required'"
          class="for-input inpType"
          :class="{ 'isInvalid': errors.has('validateCode')}"
          type="text"
          name="validateCode"
          autocomplete="off"
          :placeholder="$t('Bao.user_verify_code_notice')"
        >
        <div v-if="errors.has('validateCode')" class="input-feedback text-align-R">
          {{ $t("Bao.user_verify_code_error") }}
        </div>
      </div>

      <div class="info-row">
        <span class="icon-alert-dot">!</span>
        <span class="info-text">{{ $t(`Bao.user_verify_code_info_1_${mode}`) }}</span>
      </div>

      <b class="color-primary verify-note-bold">
        {{ $t('Bao.user_verify_code_info_2') }}
      </b>
      <p class="verify-note-light">
        {{ $t('Bao.user_verify_code_info_3') }}
      </p>

      <div class="authen-btn-wrap">
        <button class="btn-primary resend-btn" :disabled="timerCount" @click.prevent="sendCode()">
          <span>{{ timerCount ? '' + limitSecond + 's' : $t('Bao.user_verify_code_resend') }}</span>
        </button>
      </div>

      <hr class="section-sep">

      <!-- 密碼設定區塊 -->
      <!-- 登入密碼 -->
      <template v-if="login">
        <div class="password-field">
          <label class="password-label">{{ $t(`Bao.user_verify_new_password_label_login`) }}</label>
          <div class="verify-input-wrap">
            <input
              ref="newPassword"
              v-model="newPassword"
              v-validate="'min:6'"
              class="for-input inpType"
              :class="{ 'isInvalid': pwdError }"
              type="password"
              :placeholder="$t(`Bao.user_verify_new_password_notice_${type}`)"
              name="newPassword"
              @input="pwdCheck"
            >
            <div v-if="pwdError" class="input-feedback text-align-R">
              {{ pwdError }}
            </div>
          </div>
        </div>

        <div class="password-field">
          <label class="password-label">{{ $t(`Bao.user_verify_new_password_label_confirm`) }}</label>
          <div class="verify-input-wrap">
            <input
              v-model="confirmPassword"
              v-validate="'min:6|confirmed:newPassword'"
              class="for-input inpType"
              :class="{ 'isInvalid': errors.has('confirmPassword') }"
              type="password"
              :placeholder="$t(`Bao.user_verify_new_password_notice_confirm`)"
              name="confirmPassword"
            >
            <div v-if="errors.has('confirmPassword')" class="input-feedback text-align-R">
              {{ $t("Bao.user_verify_new_password_confirm_error") }}
            </div>
          </div>
        </div>
      </template>
      <template v-else>
        <!-- 提款密碼 -->
        <div class="password-field">
          <label class="password-label">{{ $t(`Bao.user_verify_new_password_label_withdrawal`) }}</label>
          <div class="verify-input-wrap">
            <input
              v-model="newPassword"
              v-validate="'required|integer'"
              class="for-input inpType"
              :class="{ 'isInvalid': errors.has('withdrawal_pass') }"
              type="password"
              :placeholder="$t(`Bao.user_verify_new_password_notice_${type}`)"
              name="withdrawal_pass"
              maxlength="4"
            >
            <div v-if="errors.has('withdrawal_pass')" class="input-feedback text-align-R">
              {{ $t('Bao.withdrawal_password_limit_error') }}
            </div>
          </div>
        </div>
      </template>

      <!-- 送出按鈕 -->
      <div class="form-button password-save-btn-wrap">
        <button class="btn-primary password-save-btn" :disabled="isSaveDisabled" @click.prevent="handleSave()">
          <span>{{ $t("Bao.user_verify_new_password_save") }}</span>
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import { getVerifyCode, update } from '@/api/user'
import { showNotice } from '@/utils/tool'
import { validatePassword } from '@/utils/validate'
import { mapGetters } from 'vuex'

export default {
  name: 'UserVerifyPasswordModal',
  props: {
    type: {
      type: String,
      default: 'login' // 'login' | 'withdrawal'
    }
  },
  data() {
    return {
      limitSecond: 60,
      timerCount: true,
      validateCode: '',
      isReady: false,
      newPassword: '',
      confirmPassword: '',
      pwdError: '',
      isPwdValid: true
    }
  },
  computed: {
    ...mapGetters(['memberInfo', 'permissionInfo']),
    email_need_verify() {
      return this.permissionInfo.email_need_verify
    },
    mode() {
      // 是開啟信箱驗證,還是手機驗證
      return this.email_need_verify ? 'email' : 'phone'
    },
    login() {
      // 是修改登入密碼,還是提款密碼
      return this.type === 'login'
    },
    isSaveDisabled() {
      if (!this.validateCode) return true
      if (!this.newPassword) return true
      if (this.login && !this.confirmPassword) return true
      if (this.login && !this.isPwdValid) return true
      if (this.errors.any()) return true
      if (!this.login && !/^\d{4}$/.test(this.newPassword)) return true
      return false
    }
  },
  created() {
    this.sendCode()
  },
  methods: {
    sendCode() {
      getVerifyCode({ username: this.memberInfo.username, verify_type: this.type, media: this.mode })
        .then(response => {
          const { code, message } = response.data.data
          if (code) {
            const i18nKey = 'Bao.user_verify_code_res_' + code
            showNotice(this.$te(i18nKey) ? this.$t(i18nKey) : message)
          }

          this.timerCount = true
          this.isReady = true
          this.countDown()
        })
        .catch((error) => {
          showNotice(error.response.data.msg)
          this.$emit('close')
        })
    },
    countDown() {
      const timer = setInterval(() => {
        if (this.limitSecond > 0) {
          this.limitSecond--
        } else {
          this.timerCount = false
          this.limitSecond = 60
          clearInterval(timer)
        }
      }, 1000)
    },
    pwdCheck() {
      const { valid, error } = validatePassword(this.newPassword, this.$t.bind(this))
      this.isPwdValid = valid
      this.pwdError = error
    },
    async handleSave() {
      const valid = await this.$validator.validateAll()
      if (!valid || (!this.login && !this.isPwdValid)) return

      const params = {
        ...(this.login
          ? { newPassword: this.newPassword, confirmPassword: this.confirmPassword }
          : { withdrawal_pass: this.newPassword }
        ),
        ...({ verify_type: this.type, verify_code: this.validateCode })
      }

      update(params).then(response => {
        showNotice(this.$t('Bao.update_success'))
        this.$store.commit('SET_MEMBER_INFO', response.data.data.member_info)
        this.$emit('close')
      }).catch((error) => {
        showNotice(error.response.data.msg)
      })
    }
  }
}
</script>

